import { useState } from 'react'
import Card from '../../../components/common/Card/Card'
import Badge from '../../../components/common/Badge/Badge'
import Button from '../../../components/common/Button/Button'
import {
  formatAnnouncementDate,
  getCategoryBadgeVariant,
  truncateContent
} from '../../../services/announcementsService'

/**
 * AnnouncementCard Component
 * Display individual announcement with expand/collapse and admin actions
 *
 * @param {object} announcement - Announcement data
 * @param {boolean} isAdmin - Whether current user is admin
 * @param {function} onEdit - Edit callback (admin only)
 * @param {function} onDelete - Delete callback (admin only)
 */
const AnnouncementCard = ({ announcement, isAdmin = false, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const isUrgent = announcement.priority === 'Urgent'
  const needsTruncation = announcement.content && announcement.content.length > 150

  const handleToggleExpand = () => {
    if (needsTruncation) {
      setIsExpanded(!isExpanded)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
      onDelete(announcement.id)
    }
  }

  return (
    <Card
      style={{
        border: isUrgent ? '2px solid var(--psyber-error)' : undefined,
        background: isUrgent ? 'linear-gradient(135deg, rgba(255, 71, 87, 0.03) 0%, rgba(255, 107, 179, 0.05) 100%)' : undefined
      }}
    >
      {/* Header with Badges */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', flex: 1 }}>
          <Badge
            variant={getCategoryBadgeVariant(announcement.category)}
            icon="fas fa-tag"
          >
            {announcement.category}
          </Badge>
          {isUrgent && (
            <Badge
              variant="error"
              icon="fas fa-exclamation-triangle"
            >
              Urgent
            </Badge>
          )}
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => onEdit(announcement)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--psyber-primary)',
                fontSize: 'var(--psyber-font-size-base)',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--psyber-radius-sm)',
                transition: 'var(--psyber-transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--psyber-card-hover)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none'
              }}
              title="Edit announcement"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              onClick={handleDelete}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--psyber-error)',
                fontSize: 'var(--psyber-font-size-base)',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--psyber-radius-sm)',
                transition: 'var(--psyber-transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--psyber-error-bg)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none'
              }}
              title="Delete announcement"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        )}
      </div>

      {/* Title */}
      <h3 style={{
        margin: '0 0 0.75rem 0',
        fontSize: 'var(--psyber-font-size-xl)',
        color: 'var(--psyber-text)',
        lineHeight: 1.3
      }}>
        {announcement.title}
      </h3>

      {/* Content */}
      <div style={{
        marginBottom: '1rem',
        color: 'var(--psyber-text-secondary)',
        fontSize: 'var(--psyber-font-size-base)',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        {isExpanded || !needsTruncation
          ? announcement.content
          : truncateContent(announcement.content, 150)}
      </div>

      {/* Read More/Less Button */}
      {needsTruncation && (
        <button
          onClick={handleToggleExpand}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--psyber-primary)',
            fontSize: 'var(--psyber-font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '0.25rem 0',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'var(--psyber-transition-fast)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--psyber-primary-dark)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--psyber-primary)'
          }}
        >
          {isExpanded ? (
            <>
              <span>Read Less</span>
              <i className="fas fa-chevron-up"></i>
            </>
          ) : (
            <>
              <span>Read More</span>
              <i className="fas fa-chevron-down"></i>
            </>
          )}
        </button>
      )}

      {/* Footer - Posted By & Date */}
      <div style={{
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--psyber-card-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
        fontSize: 'var(--psyber-font-size-sm)',
        color: 'var(--psyber-text-muted)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-user-circle"></i>
          <span>Posted by <strong>{announcement.postedByName}</strong></span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-clock"></i>
          <span>{formatAnnouncementDate(announcement.postedAt)}</span>
        </div>
      </div>

      {/* Updated At (if different from posted) */}
      {announcement.updatedAt &&
       announcement.updatedAt !== announcement.postedAt &&
       formatAnnouncementDate(announcement.updatedAt) !== formatAnnouncementDate(announcement.postedAt) && (
        <div style={{
          marginTop: '0.5rem',
          fontSize: 'var(--psyber-font-size-xs)',
          color: 'var(--psyber-text-muted)',
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-pen"></i>
          <span>Updated: {formatAnnouncementDate(announcement.updatedAt)}</span>
        </div>
      )}
    </Card>
  )
}

export default AnnouncementCard
